import React, { useMemo, memo } from "react";
import TeamIcon from "app/components/Icons/MultiplePeopleIcon";
import BellIcon from "app/components/Icons/BellIcon";
import EmptyPicture from "../../components/common/EmptyPicture";
import normalize from "app/utils/normalize";
import FastImage from "react-native-fast-image";
import theme from "app/Theme/Theme";
import HomeLightIcon from "app/components/Icons/HomeLightIcon";
import ProgressIcon from "app/components/Icons/ProgressIcon";
import MessagesIcon from "app/components/Icons/MessagesIcon";
import SettingsIcon from "app/components/Icons/SettingsIcon";
import CoachingIcon from "../icons/CoachingIcon";
import UsersIcon from "app/components/Icons/UsersIcon";
import { Box } from "app/components/common";

const PictureTabBar = memo(
  ({
    isActive,
    userPicture,
    firstName,
  }: {
    isActive: boolean;
    firstName: string;
    userPicture: string;
  }) => {
    const source = useMemo(() => {
      return {
        uri: userPicture,
      };
    }, [userPicture]);

    const style = useMemo(() => {
      return [styles.userPicture, isActive && styles.activePicture];
    }, [isActive]);

    return (
      <Box height={normalize(22)} width={normalize(22)}>
        {!!userPicture ? (
          <FastImage source={source} style={style} />
        ) : (
          <EmptyPicture
            firstName={firstName}
            size={normalize(22)}
            variant="l5-bold"
          />
        )}
      </Box>
    );
  },
);

export const TabBarIcon = (
  icon: string,
  userPicture: string,
  firstName: string,
) => ({ isActive }: { isActive: boolean }) => {
  switch (icon) {
    case "UserProfile":
      return (
        <PictureTabBar
          firstName={firstName}
          userPicture={userPicture}
          isActive={isActive}
        />
      );
    case "Home":
      return (
        <HomeLightIcon
          height={normalize(22)}
          width={normalize(22)}
          color={
            isActive ? theme.colors["brand.500"] : theme.colors["neutral.600"]
          }
        />
      );
    case "Team":
      return (
        <TeamIcon
          height={normalize(22)}
          width={normalize(22)}
          color={
            isActive ? theme.colors["brand.500"] : theme.colors["neutral.600"]
          }
        />
      );
    case "Progress":
      return (
        <ProgressIcon
          height={normalize(22)}
          width={normalize(22)}
          color={
            isActive ? theme.colors["brand.500"] : theme.colors["neutral.600"]
          }
        />
      );
    case "Coaching":
      return (
        <UsersIcon
          height={normalize(22)}
          width={normalize(22)}
          color={
            isActive ? theme.colors["brand.500"] : theme.colors["neutral.600"]
          }
        />
      );
    case "Notification":
      return (
        <BellIcon
          testID={"notification-icon"}
          height={normalize(22)}
          width={normalize(22)}
          color={
            isActive ? theme.colors["brand.500"] : theme.colors["neutral.600"]
          }
        />
      );
    default:
      throw "The candidate icon doesn't exist or is out of scope";
  }
};

export const TabBarIconDesktop = (icon: string, color: string) => {
  switch (icon) {
    case "homelight":
      return (
        <HomeLightIcon
          height={normalize(22)}
          width={normalize(22)}
          color={color}
        />
      );
    case "progress":
      return (
        <ProgressIcon
          height={normalize(22)}
          width={normalize(22)}
          color={color}
        />
      );
    case "team":
      return (
        <TeamIcon height={normalize(22)} width={normalize(22)} color={color} />
      );
    case "messages":
      return (
        <MessagesIcon
          height={normalize(22)}
          width={normalize(22)}
          color={color}
        />
      );
    case "ios-notifications":
      return (
        <BellIcon
          testID={"notification-icon"}
          height={normalize(22)}
          width={normalize(22)}
          color={color}
        />
      );
    case "settings":
      return (
        <SettingsIcon
          testID={"notification-icon"}
          height={normalize(22)}
          width={normalize(22)}
          color={color}
        />
      );
    default:
      throw "The candidate icon doesn't exist or is out of scope";
  }
};

const styles = {
  userPicture: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: normalize(22),
    height: normalize(22),
    borderRadius: normalize(200),
    borderColor: theme.colors["brand.500"],
  },
  activePicture: {
    borderWidth: 1,
  },
};
